import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { StockCategoryFilter } from '../types/stockCategories';
import { isStartOverlayDismissed, setStartOverlayDismissed, clearStartOverlayDismissed } from '../utils/startOverlayStorage';

interface GameStartContextType {
  /** Whether the game has started (user clicked Play on the start overlay) */
  hasGameStarted: boolean;
  /** Mark the game as started for the current category */
  startGame: () => void;
  /** Go back to the start overlay for the current category */
  goBack: () => void;
  /** Update the current category (resets hasGameStarted based on localStorage) */
  setCategory: (category: StockCategoryFilter, isReplayMode?: boolean) => void;
}

const GameStartContext = createContext<GameStartContextType | undefined>(undefined);

interface GameStartProviderProps {
  children: ReactNode;
}

/**
 * Provider for managing the game start overlay state.
 * Tracks whether the user has clicked "Play" to start the game for each category/day.
 */
export const GameStartProvider: React.FC<GameStartProviderProps> = ({ children }) => {
  const [hasGameStarted, setHasGameStarted] = useState(true); // Default to true for backward compatibility
  const [currentCategory, setCurrentCategory] = useState<StockCategoryFilter | null>(null);

  const setCategory = useCallback((category: StockCategoryFilter, isReplayMode = false) => {
    setCurrentCategory(category);

    // In replay mode, skip the overlay
    if (isReplayMode) {
      setHasGameStarted(true);
      return;
    }

    // Check localStorage to see if overlay was already dismissed for this category/day
    setHasGameStarted(isStartOverlayDismissed(category));
  }, []);

  const startGame = useCallback(() => {
    if (currentCategory) {
      setStartOverlayDismissed(currentCategory);
      setHasGameStarted(true);
    }
  }, [currentCategory]);

  const goBack = useCallback(() => {
    if (currentCategory) {
      clearStartOverlayDismissed(currentCategory);
      setHasGameStarted(false);
    }
  }, [currentCategory]);

  const value: GameStartContextType = {
    hasGameStarted,
    startGame,
    goBack,
    setCategory,
  };

  return (
    <GameStartContext.Provider value={value}>
      {children}
    </GameStartContext.Provider>
  );
};

/**
 * Hook to access the game start context.
 * Must be used within a GameStartProvider.
 */
export const useGameStart = (): GameStartContextType => {
  const context = useContext(GameStartContext);
  if (context === undefined) {
    throw new Error('useGameStart must be used within a GameStartProvider');
  }
  return context;
};

export default GameStartProvider;
